<template>
  <!-- <VaButtonToggle v-model="theme" color="background-element" border-color="background-element" :options="options" /> -->
  <div class="glass-toggle">
    <button
      v-for="opt in options"
      :key="opt.value"
      :class="['toggle-btn', { active: theme === opt.value }]"
      @click="theme = opt.value"
    >
      <span>{{ opt.value === 'dark' ? '🌙' : '☀️' }}</span>
    </button>
  </div>
</template>

<script lang="ts" setup>
import { computed, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

// Helper: sessionStorage for agents (impersonation or direct login), localStorage for admin
const getStorage = (): Storage => {
  // Check if it's impersonation session
  if (sessionStorage.getItem('impersonation_session')) {
    return sessionStorage
  }
  
  // Check if it's agent direct login
  if (sessionStorage.getItem('admin_token') && sessionStorage.getItem('role') === 'agent') {
    return sessionStorage
  }
  
  // Default: admin using localStorage
  return localStorage
}

const { applyPreset, currentPresetName } = useColors()
const theme = computed({
  get: () => currentPresetName.value,
  set: (v) => {
    applyPreset(v)
    // Synchronize with Tailwind dark mode
    if (v === 'dark') {
      document.documentElement.classList.add('dark')
    } else {
      document.documentElement.classList.remove('dark')
    }
    // Store preference in appropriate storage
    const storage = getStorage()
    storage.setItem('app_local_theme', v)
    
    // Émettre un événement pour notifier le changement de thème
    window.dispatchEvent(new CustomEvent('themeChanged', { detail: { theme: v } }))
  },
})

// Initialize theme on mount
const initializeTheme = () => {
  const storage = getStorage()
  const storedTheme = storage.getItem('app_local_theme')
  const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  const initialTheme = storedTheme || systemTheme

  theme.value = initialTheme
}

// Watch for system theme changes
watch(
  () => window.matchMedia('(prefers-color-scheme: dark)').matches,
  (isDark) => {
    const storage = getStorage()
    if (!storage.getItem('app_local_theme')) {
      theme.value = isDark ? 'dark' : 'light'
    }
  },
)

// Initialize on component mount
initializeTheme()

const { t } = useI18n()
const options = [
  { value: 'dark' },
  { value: 'light' },
]
</script>

<style scoped>
.glass-toggle {
  display: flex;
  gap: 0.375rem;
  padding: 0.375rem;
  border-radius: 1rem;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: fit-content;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.toggle-btn {
  width: 2.125rem;
  height: 2.125rem;
  border: none;
  border-radius: 0.75rem;
  background: transparent;
  cursor: pointer;
  font-size: 1.0625rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
}

.toggle-btn.active {
  background: rgba(255, 255, 255, 0.3);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transform: scale(1.03);
}

.toggle-btn:hover:not(.active) {
  background: rgba(255, 255, 255, 0.15);
  transform: scale(1.02);
}

.toggle-btn span {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: inline-block;
}

.toggle-btn.active span {
  animation: bounce 0.6s ease-in-out;
}

@keyframes bounce {
  0%, 100% {
    transform: translateY(0) scale(1);
  }
  25% {
    transform: translateY(-4px) scale(1.1);
  }
  50% {
    transform: translateY(0) scale(0.95);
  }
  75% {
    transform: translateY(-2px) scale(1.05);
  }
}

@media (max-width: 640px) {
  .glass-toggle {
    gap: 0.25rem;
    padding: 0.3125rem;
  }

  .toggle-btn {
    width: 1.95rem;
    height: 1.95rem;
    border-radius: 0.6875rem;
    font-size: 1rem;
  }
}

@media (max-width: 480px) {
  .glass-toggle {
    padding: 0.25rem;
  }

  .toggle-btn {
    width: 1.8rem;
    height: 1.8rem;
    border-radius: 0.625rem;
  }
}
</style>
